import { useRef, useState, type RefObject } from 'react';

import {
  type IScreensComponent,
  type ScreensReadModel,
} from '../../api';
import { List, type ListRef } from '@banhala/ui-kit';
import { delay, isWeb } from '../../utils';
import Animated, {
  useAnimatedScrollHandler,
  useSharedValue,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { BrandHomeScrollToTop } from './BrandHomeScrollToTop';
import { useRoute } from '@react-navigation/native';
import { useLikeMap } from '../../hooks';
import { createAtomicComponent } from './utils/createAtomicComponent';

export interface AtomicRendererProps {
  listRef: RefObject<ListRef<IScreensComponent> | null>;
  data: ScreensReadModel;
  fetchNextPage: VoidFunction;
  filterListIndex?: number;
  scrollToFilterList: VoidFunction;
  isFetching: boolean;
  refetch: VoidFunction;
}

const AnimatedList = Animated.createAnimatedComponent(List<IScreensComponent>);

/**
 * @example
<FilterStateProvider>
<AtomicRenderer
  data={data}
  fetchNextPage={fetchNextPage}
/>
</FilterStateProvider>
 */
export const AtomicRenderer = ({
  listRef,
  data,
  fetchNextPage,
  filterListIndex,
  scrollToFilterList,
  isFetching,
  refetch,
}: AtomicRendererProps) => {
  const atomicRendererScrollY = useSharedValue(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const prevContentSizeHeightRef = useRef<number>(0);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { params } = useRoute<any>();
  const { likeMap, setLikeMap } = useLikeMap()

  const onScrollToIndexFailed = async (info: { index: number }) => {
    await delay(300);
    listRef.current?.scrollToIndex({
      index: info.index,
      animated: false,
    });
  };

  const handleScroll = useAnimatedScrollHandler(
    ({ contentSize, layoutMeasurement, contentOffset }) => {
      atomicRendererScrollY.value = contentOffset.y;

      if (
        isFetching ||
        prevContentSizeHeightRef.current === contentSize.height
      ) {
        return;
      }
      const distanceFromBottom =
        contentSize.height - layoutMeasurement.height - contentOffset.y;
      if (distanceFromBottom < layoutMeasurement.height * 3) {
        // FlatList가 화면을 그리기전에 scroll을 하면 계속 fetchNextPage를 호출하여 이를 방지.
        // 앱에서만 해당 문제가 발생
        prevContentSizeHeightRef.current = contentSize.height;
        scheduleOnRN(fetchNextPage);
      }
    },
  );

  const onRefresh = () => {
    setIsRefreshing(true);
    // TODO: refetch를 할 때 pagination한만큼의 api를 모두 호출하는 이슈가 있음
    refetch();
    setIsRefreshing(false);
  };
  return (
    <>
      <AnimatedList
        ref={listRef}
        data={data.components}
        showsVerticalScrollIndicator={false}
        stickyHeaderIndices={filterListIndex ? [filterListIndex] : undefined}
        onScroll={handleScroll}
        renderItem={({ index, item }) => {
          return createAtomicComponent({
            component: item,
            index,
            likeMap,
            setLikeMap,
            scrollToFilterList,
          });
        }}
        refreshing={isRefreshing}
        onRefresh={onRefresh}
        disableVirtualization={isWeb}
        onScrollToIndexFailed={onScrollToIndexFailed}
      />
      {params?.screen_name === 'BRAND_HOME' && (
        <BrandHomeScrollToTop
          atomicRendererScrollY={atomicRendererScrollY}
          listRef={listRef}
        />
      )}
    </>
  );
};
